import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';
import {
	Box,
	Typography,
	IconButton,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Switch,
	Button,
	Chip,
	CircularProgress,
	Alert,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { usePrompt, usePromptTypes } from '../../hooks/Prompt';
import PromptDeleteDialog from './PromptDeleteDialog';
import { StyledDrawer } from '../common/StyledDrawer';

const EMPTY_PROMPT = {
	name: '',
	content: '',
	prompt_type: '',
	active: true,
	version: 1,
};

const PromptManageDrawer = ({ open, onClose, prompt, onUpdateSuccess }) => {
	const { tenantId } = useParams();
	const theme = useTheme();
	const [formData, setFormData] = useState(EMPTY_PROMPT);
	const [submitError, setSubmitError] = useState('');
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { loading, createPrompt, updatePrompt, deletePrompt } =
		usePrompt(tenantId);

	const { promptTypes, fetchPromptTypes } = usePromptTypes(tenantId);

	// Fetch prompt types
	useEffect(() => {
		if (open) fetchPromptTypes();
	}, [open, fetchPromptTypes]);

	// Initialize form when prompt or prompt types change
	useEffect(() => {
		if (!open) return;

		if (prompt) {
			const promptTypeId =
				prompt.promptTypeId || prompt._original?.prompt_type_id;
			const type = promptTypes.find(
				(t) => t.id === promptTypeId || t.name === prompt.promptType
			);

			setFormData({
				name: prompt.name || '',
				content: prompt.content || '',
				prompt_type: type?.id || promptTypeId || '',
				active: prompt.active ?? true,
				version: prompt.version || 1,
			});
		} else {
			setFormData(EMPTY_PROMPT);
		}
	}, [open, prompt, promptTypes]);

	const handleChange = useCallback((e) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
	}, []);

	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			setSubmitError('');

			const { name, content, prompt_type } = formData;
			if (!name?.trim() || !content?.trim() || !prompt_type) {
				setSubmitError('Please fill in all required fields');
				return;
			}

			try {
				const promptData = {
					name: name.trim(),
					content: content.trim(),
					prompt_type: prompt_type,
					active: formData.active,
				};

				const result = prompt
					? await updatePrompt(prompt.id, promptData)
					: await createPrompt(promptData);

				onUpdateSuccess(result, prompt ? 'update' : 'create');
				onClose();
			} catch (err) {
				setSubmitError('Failed to save prompt. Please try again.');
				console.error('Save error:', err);
			}
		},
		[formData, prompt, createPrompt, updatePrompt, onUpdateSuccess, onClose]
	);

	const handleDeleteClick = useCallback(() => {
		if (!prompt) return;
		setDeleteDialogOpen(true);
	}, [prompt]);

	const handleConfirmDelete = useCallback(
		async (promptToDelete) => {
			try {
				await deletePrompt(promptToDelete.id);
				onUpdateSuccess(promptToDelete, 'delete');
				onClose();
			} catch (err) {
				setSubmitError('Failed to delete prompt. Please try again.');
				console.error('Delete error:', err);
			}
		},
		[deletePrompt, onUpdateSuccess, onClose]
	);

	const title = prompt ? 'Edit Prompt' : 'Create New Prompt';
	const isFormValid =
		formData.name?.trim() && formData.content?.trim() && formData.prompt_type;
	const isLoading = loading;

	return (
		<>
			<StyledDrawer
				anchor='right'
				open={open}
				onClose={onClose}
				isMobile={isMobile}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
					{/* Header */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							borderBottom: `1px solid ${theme.palette.divider}`,
							p: 2,
						}}
					>
						<Box display='flex' alignItems='center'>
							<Typography variant='h6'>{title}</Typography>
							{prompt?.version && (
								<Chip
									label={`v${formData.version}`}
									size='small'
									sx={{ ml: 2 }}
								/>
							)}
						</Box>
						<IconButton onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Box>

					{/* Content */}
					<Box
						component='form'
						onSubmit={handleSubmit}
						noValidate
						sx={{ flex: 1, overflowY: 'auto', p: 2 }}
					>
						{submitError && (
							<Alert severity='error' sx={{ mb: 2 }}>
								{submitError}
							</Alert>
						)}

						<TextField
							fullWidth
							margin='normal'
							label='Name'
							name='name'
							value={formData.name}
							onChange={handleChange}
							required
							disabled={isLoading}
						/>

						<FormControl fullWidth margin='normal' required>
							<InputLabel>Prompt Type</InputLabel>
							<Select
								name='prompt_type'
								value={formData.prompt_type}
								onChange={handleChange}
								label='Prompt Type'
								disabled={isLoading || !promptTypes.length}
							>
								{promptTypes.map((type) => (
									<MenuItem key={type.id} value={type.id}>
										{type.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<TextField
							fullWidth
							margin='normal'
							label='Content'
							name='content'
							value={formData.content}
							onChange={handleChange}
							multiline
							minRows={4}
							required
							disabled={isLoading}
						/>

						<FormControlLabel
							control={
								<Switch
									checked={formData.active}
									onChange={handleChange}
									name='active'
									color='primary'
									disabled={isLoading}
								/>
							}
							label='Active'
						/>
					</Box>

					{/* Footer */}
					<Box
						display='flex'
						justifyContent='space-between'
						width='100%'
						p={2}
						pr={4}
						mt='auto'
					>
						<div>
							{prompt && (
								<Button
									onClick={handleDeleteClick}
									color='error'
									disabled={isLoading}
									startIcon={isLoading ? <CircularProgress size={20} /> : null}
								>
									{isLoading ? 'Deleting...' : 'Delete'}
								</Button>
							)}
						</div>
						<Box>
							<Button onClick={onClose} sx={{ mr: 1 }} disabled={isLoading}>
								Cancel
							</Button>
							<Button
								onClick={handleSubmit}
								variant='contained'
								color='primary'
								disabled={isLoading || !isFormValid}
								startIcon={isLoading ? <CircularProgress size={20} /> : null}
							>
								{isLoading ? 'Saving...' : prompt ? 'Update' : 'Create'}
							</Button>
						</Box>
					</Box>
				</Box>
			</StyledDrawer>

			<PromptDeleteDialog
				open={deleteDialogOpen}
				prompt={prompt}
				onClose={() => setDeleteDialogOpen(false)}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};

PromptManageDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onUpdateSuccess: PropTypes.func.isRequired,
	prompt: PropTypes.shape({
		id: PropTypes.string,
		name: PropTypes.string,
		content: PropTypes.string,
		promptType: PropTypes.string,
		promptTypeId: PropTypes.string,
		version: PropTypes.number,
		active: PropTypes.bool,
		_original: PropTypes.object,
	}),
};

export default PromptManageDrawer;
